/**
 * Service worker registration + install-prompt plumbing.
 *
 * Import this file once at app bootstrap (main.tsx). The SW script lives at
 * `/sw.js` and controls scope `/`. The `beforeinstallprompt` event is stashed
 * so the UI can trigger `triggerInstall()` from anywhere.
 */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;

const INSTALLABLE_EVENT = "mavinty-installable-changed";

function emitInstallable() {
  window.dispatchEvent(new CustomEvent(INSTALLABLE_EVENT, { detail: canInstall() }));
}

export function canInstall(): boolean {
  return deferredPrompt !== null;
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export async function triggerInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  try {
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    emitInstallable();
    return choice.outcome;
  } catch {
    deferredPrompt = null;
    emitInstallable();
    return "dismissed";
  }
}

export function onInstallableChange(cb: (installable: boolean) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<boolean>).detail);
  window.addEventListener(INSTALLABLE_EVENT, handler);
  // Emit current state once.
  queueMicrotask(() => cb(canInstall()));
  return () => window.removeEventListener(INSTALLABLE_EVENT, handler);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emitInstallable();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emitInstallable();
    // Optional analytics hook
    if (typeof console !== "undefined") {
      console.log("[PWA] Mavinty installed");
    }
  });

  // Register SW only on production origins to avoid dev-server churn.
  if ("serviceWorker" in navigator && location.protocol === "https:") {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js", { scope: "/" })
        .then((registration) => {
          // Soft update check hourly while the tab is open.
          setInterval(() => registration.update().catch(() => undefined), 60 * 60 * 1000);
        })
        .catch((err) => {
          // Don't block the app on SW failures.
          console.warn("[PWA] Service worker registration failed:", err);
        });
    });
  }
}
